import React from "react";
import OverviewKpiGrid from "./OverviewKpiGrid";
import OverviewPaymentsCard from "./OverviewPaymentsCard";
import OverviewWarningsCard from "./OverviewWarningsCard";
import OverviewDataSourceCard from "./OverviewDataSourceCard";
import OverviewTrendCard from "./OverviewTrendCard";
import { useOverviewData } from "./useOverviewData";

/**
 * The company-overview tab.
 *
 * Reads nothing from the legacy KPI world. `/dashboard/kpis` folds unrecognised cost
 * rows into `summary.total_costs` and derives its profit and margin from that total,
 * so every figure downstream of it carries production's free-text "Kaution" row as an
 * operating expense. This surface is built on the A1/A2 contracts instead, which
 * classify fail-closed and keep the unclassified remainder visible and separate.
 *
 * LAYOUT
 * ------
 * Three bands, in the order the reference reads: the KPI grid, then the revenue
 * trend, then the activity row (recent payments wide, warnings and data sources
 * stacked beside them).
 *
 * THE COST STRUCTURE MOVED TO FINANZEN in F2c. It used to sit beside the trend in a
 * 3-column grid; with it gone that grid would leave the trend spanning two of three
 * columns and the third empty, so the trend simply takes the full width. This tab is
 * the compact management summary — the split by bucket, the receipts chart and the
 * per-unit cost ranking are analysis and now live together on Finanzen, where they
 * read the same A2 contract. It was MOVED, not copied: two cards rendering these five
 * categories would be one number with two homes.
 *
 * The trend costs no request. It reads `revenue-analytics.trend`, already in the
 * response behind the KPI cards — so this band adds a picture, not traffic. The A2
 * breakdown is still fetched here for the "Betriebskosten" KPI card.
 *
 * THE PERIOD (P3b)
 * ----------------
 * `period` is now the GLOBAL `{mode, from, to}`, not the to-month the page used to
 * reduce it to. The hook resolves it once into the window the P3a contracts take and
 * hands that window back, so every card below labels and requests the SAME period —
 * there is no second resolution anywhere in this tree, and no card derives a month of
 * its own.
 *
 * Which cards move with it is a deliberate split, not an implementation detail: the
 * KPI grid's four headline figures, cash-in, the management occupancy line, the trend
 * and the payment feed answer for the period; open receivables, the live occupancy
 * snapshot, cash deposits, the warnings and the data-source status answer for today
 * and are handed no period at all.
 */
export default function OverviewTab({
  period,
  active = true,
  paymentsLimit = 5,
  // Tenant cash deposits and their details flow stay owned by the page: it already
  // loads the figure and holds the modal's state. Passing them down keeps one owner
  // for a working feature instead of duplicating the read and the modal here.
  cashDeposits = null,
  cashDepositsLoading = false,
  onOpenCashDepositDetails,
}) {
  const {
    revenueAnalytics,
    companyInvoiced,
    costBreakdown,
    occupancyRoomDays,
    currentOccupancy,
    recentPayments,
    bexioStatus,
    alerts,
    criticalUnits,
    periodicLoading,
    currentLoading,
    window: periodWindow,
    unsupported,
    reloadPeriodic,
    reloadCurrent,
  } = useOverviewData(period, { enabled: active, paymentsLimit });

  return (
    <div className="space-y-4" data-testid="company-overview-tab">
      <OverviewKpiGrid
        revenueAnalytics={revenueAnalytics}
        companyInvoiced={companyInvoiced}
        costBreakdown={costBreakdown}
        occupancyRoomDays={occupancyRoomDays}
        currentOccupancy={currentOccupancy}
        window={periodWindow}
        unsupported={unsupported}
        cashDeposits={cashDeposits}
        cashDepositsLoading={cashDepositsLoading}
        onOpenCashDepositDetails={onOpenCashDepositDetails}
        periodicLoading={periodicLoading}
        currentLoading={currentLoading}
        onRetryPeriodic={reloadPeriodic}
        onRetryCurrent={reloadCurrent}
      />

      <OverviewTrendCard
        revenueAnalytics={revenueAnalytics}
        window={periodWindow}
        unsupported={unsupported}
        loading={periodicLoading}
        onRetry={reloadPeriodic}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <OverviewPaymentsCard
            payments={recentPayments}
            window={periodWindow}
            loading={periodicLoading}
            onRetry={reloadPeriodic}
          />
        </div>
        <div className="space-y-4">
          <OverviewWarningsCard
            alerts={alerts}
            criticalUnits={criticalUnits}
            loading={currentLoading}
            onRetry={reloadCurrent}
          />
          <OverviewDataSourceCard bexioStatus={bexioStatus} loading={currentLoading} />
        </div>
      </div>
    </div>
  );
}
